import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'

export type School = Database['public']['Tables']['schools']['Row']
export type SchoolInsert = Database['public']['Tables']['schools']['Insert']
export type SchoolUpdate = Database['public']['Tables']['schools']['Update']

export class SchoolService {
  private static readonly LOGO_BUCKET = 'school-logos'
  // Get all schools
  static async getSchools(): Promise<School[]> {
    const { data, error } = await supabase
      .from('schools')
      .select('*')
      .order('name')

    if (error) throw error
    return data || []
  }

  // Get school by ID
  static async getSchool(id: string): Promise<School | null> {
    const { data, error } = await supabase
      .from('schools')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  }



  // Get users for a school
  static async getSchoolUsers(schoolId: string): Promise<Array<{
    id: string
    email: string
    name: string
    role: string
    grade: string | null
    class: string | null
    created_at: string
    last_login: string | null
  }>> {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, name, role, grade, class, created_at, last_login')
      .eq('school_id', schoolId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // Admin: Create new school
  static async createSchool(school: SchoolInsert): Promise<School> {
    const { data, error } = await supabase
      .from('schools')
      .insert(school)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Admin: Update school
  static async updateSchool(id: string, updates: SchoolUpdate): Promise<School> {
    const { data, error } = await supabase
      .from('schools')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Admin: 管理者用の学校（is_admin_school）を切り替える。
   *
   * true の行は1つしか作れない（部分ユニークインデックス）ので、先に他校を落としてから
   * 対象を立てる。順序を逆にすると 23505 がそのままユーザーに出てしまう。
   */
  static async setAdminSchool(schoolId: string | null): Promise<void> {
    const clear = supabase.from('schools').update({ is_admin_school: false }).eq('is_admin_school', true)
    const { error: clearError } = schoolId ? await clear.neq('id', schoolId) : await clear
    if (clearError) throw clearError

    if (!schoolId) return

    const { error } = await supabase
      .from('schools')
      .update({ is_admin_school: true })
      .eq('id', schoolId)

    if (error) throw error
  }

  // Teacher: Upload school logo, returns public URL
  static async uploadSchoolLogo(schoolId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${schoolId}/logo-${Date.now()}.${fileExt}`

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(this.LOGO_BUCKET)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      })

    if (uploadError) {
      console.error('Logo upload error details:', JSON.stringify(uploadError))
      throw new Error(`Logo upload failed: ${uploadError.message}`)
    }

    const { data: { publicUrl } } = supabase.storage
      .from(this.LOGO_BUCKET)
      .getPublicUrl(uploadData.path)

    return publicUrl
  }

  // Teacher: Update own school settings (basic info + AI fields + logo)
  static async updateSchoolAsTeacher(
    id: string,
    updates: Pick<SchoolUpdate, 'name' | 'address' | 'representative' | 'phone' | 'contact_email' | 'ai_education_policy' | 'ai_response_policy' | 'logo_url' | 'logo_display_mode'>
  ): Promise<School> {
    const { data, error } = await supabase
      .from('schools')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Admin: Delete school
  static async deleteSchool(id: string): Promise<void> {
    const { error } = await supabase
      .from('schools')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Admin: Get school statistics
  static async getSchoolStats(schoolId?: string) {
    let query = supabase
      .from('users')
      .select('school_id, role, created_at')

    if (schoolId) {
      query = query.eq('school_id', schoolId)
    }

    const { data: users, error } = await query

    if (error) throw error

    const usersBySchool = (users || []).reduce((acc, user) => {
      if (!user.school_id) return acc
      
      if (!acc[user.school_id]) {
        acc[user.school_id] = {
          totalUsers: 0,
          students: 0,
          admins: 0,
          recentRegistrations: 0
        }
      }

      acc[user.school_id].totalUsers++
      
      if (user.role === 'student') {
        acc[user.school_id].students++
      } else if (user.role === 'admin' || user.role === 'super_admin') {
        acc[user.school_id].admins++
      }

      // Count registrations in last 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      if (new Date(user.created_at) > thirtyDaysAgo) {
        acc[user.school_id].recentRegistrations++
      }

      return acc
    }, {} as Record<string, {
      totalUsers: number
      students: number
      admins: number
      recentRegistrations: number
    }>)

    return usersBySchool
  }

}